import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";
import { useMemo } from "react";
import { jamRegistrationsApi } from "../api/client";
import {
  useJamEventQuery,
  useJamEventsQuery,
  useJamRegistrationsQuery,
} from "../api/queries";
import { JamRegistrationsFilter } from "../ui/JamRegistrationsFilter";
import { JamRegistrationsTable } from "../ui/JamRegistrationsTable";

type JamRegistrationsSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
  eventId?: string;
  status?: string;
  categoryId?: string;
  search?: string;
};

export function JamRegistrationsPage() {
  const nav = useNavigate({ from: "/app/jam-registrations" });
  const canExport = useActionPermission(["jam-registrations:export"]);
  const search = useSearch({
    from: "/app/jam-registrations",
  }) as JamRegistrationsSearch;

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "submittedAt",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "submittedAt", dir: "desc" }];
  }, [search.sort]);

  const { data: eventsData } = useJamEventsQuery();
  const eventId = String(search.eventId ?? eventsData?.items[0]?.id ?? "");
  const { data: eventDetail } = useJamEventQuery(eventId || undefined);

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
      eventId: eventId || undefined,
      status: search.status,
      categoryId: search.categoryId,
      search: search.search,
    }),
    [search, eventId, sortArray],
  );

  const { data, isLoading } = useJamRegistrationsQuery(query);
  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const categories =
    eventDetail?.categories
      ?.filter((c) => c.enabled)
      .map((c) => ({ id: c.id, name: c.publicLabelLao ?? c.name })) ?? [];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Dashboard", href: "/app/dashboard" },
            { label: "ໃບສະໝັກ Jam" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">ໃບສະໝັກ Jam</h1>
            <p className="text-muted-foreground text-sm">
              {eventDetail?.title ?? "Fanglao Jam"}
            </p>
          </div>
          {canExport && eventId ? (
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  jamRegistrationsApi.exportCsvUrl(eventId),
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <DownloadIcon className="mr-2 size-4" />
              ສົ່ງອອກ CSV
            </Button>
          ) : null}
        </div>

        <JamRegistrationsFilter
          status={search.status}
          categoryId={search.categoryId}
          search={search.search}
          categories={categories}
          onStatusChange={(status) =>
            nav({ search: (prev) => ({ ...prev, status, offset: 0 }) })
          }
          onCategoryChange={(categoryId) =>
            nav({ search: (prev) => ({ ...prev, categoryId, offset: 0 }) })
          }
          onSearchChange={(q) =>
            nav({ search: (prev) => ({ ...prev, search: q, offset: 0 }) })
          }
        />

        <div className="mt-6">
          <JamRegistrationsTable
            isLoading={isLoading}
            data={items}
            offset={query.offset}
            limit={query.limit}
            totalCount={total}
            sortBy={sortArray[0]?.field}
            sortOrder={sortArray[0]?.dir}
            onPaginationChange={(offset, limit) =>
              nav({ search: (prev) => ({ ...prev, offset, limit }) })
            }
            onSortingChange={(field, desc) =>
              nav({
                search: (prev) => ({
                  ...prev,
                  sort: [{ field, dir: desc ? "desc" : "asc" }],
                }),
              })
            }
            onView={(id) =>
              nav({ to: "/app/jam-registrations/$id", params: { id } })
            }
          />
        </div>
      </Main>
    </>
  );
}
